import { useEffect, useState, useContext, useMemo, useCallback, useTransition } from 'react'
import { Delete, FileCopyOutlined, Edit, AddToPhotos } from '@mui/icons-material'
import { CircularProgress, TableCell, TableRow, Checkbox, Tooltip, Fab } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { motion } from 'framer-motion'
import { SquareDashedKanban as HeaderIcon } from 'lucide-react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Paper } from '@components'
import { AdminHeader } from '@components/layout/admin-header'
import { templateContext, modalContext } from '@contexts'
import { colors } from '@global/constants'
import { useQueryString } from '@global/hooks'
import { api } from 'api'
import { getCellContent, showSnackbar } from 'functions'
import { CvsTable } from '../../components/table/cvs-table'
import { EnhancedTableToolbar } from '../../components/table/enhanced-table-toolbar'
import { Fuzzy } from '../../utils/fuse-search'
import { t } from '@lingui/core/macro'
import { node, number, string } from 'prop-types'

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '100%',
    height: '100%',
  },
  paper: {
    width: '100%',
    overflow: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 0,
  },
  table: {
    height: '100%',
    width: '100%',
    tableLayout: 'fixed',
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  textField: {
    width: 200,
  },
}))

/**
 * TableCell with tooltip.
 * Double nested div and span are required as a workaround for the tooltip from being
 * cut off by the TableCell when overflow hidden is applied. Span element (not div) is
 * necessary for ellipsis to work.
 *
 * Do not use Tooltip from MUI; it's time to re-render on large lists causes
 * a very slow latency when clicking on a row checkbox. We use a CSS only tooltip
 * solution to avoid this.
 *
 * Note: It's important to dynamically set the zIndex so the tooltip row that's hovered is
 * always above the other rows.
 *
 * @param {*} props React Props
 * @param {string} props.tooltip Tooltip text.
 * @returns
 */
const TableCellWithTooltip = ({ tooltip, rowNum, children }) => {
  const changeTooltipDirectionAtRowNumIndex = 1
  const tooltipDirection = rowNum > changeTooltipDirectionAtRowNumIndex ? 'top' : 'bottom'

  return (
    <TableCell
      scope="row"
      padding="normal"
      style={{
        whiteSpace: 'nowrap',
        position: 'relative',
      }}
    >
      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 12 }}>
        <span data-tooltip={tooltip} data-tooltip-dir={tooltipDirection}>
          <span style={{ fontSize: 12 }}>{children}</span>
        </span>
      </div>
    </TableCell>
  )
}

TableCellWithTooltip.propTypes = {
  tooltip: string.isRequired,
  rowNum: number.isRequired,
  children: node.isRequired,
}

export const TemplateList = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const classes = useStyles()
  const ModalContext = useContext(modalContext)
  const { byId, fetchTemplates, updateTemplates } = useContext(templateContext)
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState({})
  const [displayTemplates, setDisplayTemplates] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const { setUrl, setStateFromQuery } = useQueryString(searchTerm, setSearchTerm)
  const [isPending, startTransition] = useTransition()
  const templates = useMemo(
    () =>
      Object.keys(byId).map(id => ({
        ...byId[id],
        patternText: byId[id].patterns?.map(pat => pat.name).join(', '),
        fieldsetText: byId[id].fieldsets.map(fs => fs.name).join(', '),
      })),
    [byId],
  )

  const columns = [
    { label: t`Name`, width: '30%', lookup: 'name' },
    { label: t`Updated`, type: 'date', width: '10%', lookup: 'updated_at' },
    { label: t`Created`, type: 'date', width: '10%', lookup: 'created_at' },
    { label: t`Created By`, width: '10%', lookup: 'created_by_user_name' },
    { label: t`Patterns`, width: '10%', lookup: 'patternText' },
    { label: t`Field Sets`, width: '10%', lookup: 'fieldsetText' },
    { label: t`Description`, width: '20%', lookup: 'description' },
  ]

  const fuzzy = useMemo(() => new Fuzzy(templates, { keys: ['name'] }), [templates])

  useEffect(() => {
    const fetchAndUpdateTemplates = () => {
      setLoading(true)
      fetchTemplates()
        .then(updatedTemplates => {
          startTransition(() => {
            if (history.location.search) {
              setStateFromQuery(term => {
                setSearchTerm(term)
                setDisplayTemplates(fuzzy.search(term))
              })
            } else {
              setDisplayTemplates(updatedTemplates)
            }
          })
        })
        .finally(() => {
          setLoading(false)
        })
    }

    fetchAndUpdateTemplates()
  }, [])

  // Keep displayTemplates in sync with templates when they change
  useEffect(() => {
    if (searchTerm && searchTerm.trim() !== '') {
      setDisplayTemplates(fuzzy.search(searchTerm))
    } else {
      setDisplayTemplates(templates)
    }
  }, [templates, searchTerm, fuzzy])

  const clearSearch = useCallback(() => {
    startTransition(() => {
      setSearchTerm('')
      setDisplayTemplates(templates)
      setUrl()
    })
  }, [setUrl, templates, startTransition])

  const performSearch = useCallback(
    term => {
      if (!term || term.trim() === '') {
        setUrl('')
        return templates
      }

      const results = fuzzy.search(term)

      return results
    },
    [fuzzy, templates],
  )

  const handleSearchChange = useCallback(
    value => {
      setUrl({ q: value })

      startTransition(() => {
        const results = performSearch(value)
        setDisplayTemplates(results)
      })
    },
    [performSearch],
  )

  const filterChanged = useCallback(
    search => {
      handleSearchChange(search)
    },
    [handleSearchChange],
  )

  function navigateToEditTemplate(temp) {
    history.push(`/admin/templates/edit-template/${temp}/setup`)
  }

  const duplicateItem = () => {
    // TODO: make the selected better. cause it's an object.... and that kinda sucks.
    const selectedId = Object.keys(selected).find(k => selected[k] === true)
    history.push(`/admin/templates/edit-template/new/setup?duplicate=${selectedId}`)
  }

  const onReset = () => {}

  const editItems = () => {
    const selectedId = Object.keys(selected).find(k => selected[k] === true)
    history.push(`/admin/templates/edit-template/${selectedId}/setup`)
  }

  const addItem = () => {
    history.push(`/admin/templates/edit-template/new/setup`)
  }

  const deleteItems = payload => {
    api
      .post('/api/v1/account/template/bulk/delete', {
        template_ids: Object.keys(payload).filter(p => payload[p]),
      })
      .then(response => {
        const { data } = response

        const message = data ? data.message : t`Template deleted successfully`
        const poweredTemplateIds = data ? data.powered_template_ids : []

        const filteredTemplates = templates.filter(template => {
          return !payload[template.id] || poweredTemplateIds.includes(parseInt(template.id))
        })
        const updatedTemplates = filteredTemplates.reduce(
          (acc, row) => ({ ...acc, [row.id]: row }),
          {},
        )
        updateTemplates(updatedTemplates)
        setSelected({})
        clearSearch()
        dispatch(
          showSnackbar({
            message,
            type: 'SUCCESS',
            duration: 5000,
          }),
        )
      })
      .catch(() => {
        dispatch(
          showSnackbar({
            message: t`Template failed to delete`,
            type: 'ERROR',
            duration: 5000,
          }),
        )
      })
  }

  const deleteItemsModal = () => {
    ModalContext.displayModal({
      title: t`Are you sure you want to delete these Templates?`,
      description: t`Templates are a foundational part of the Claravine experience. Please be sure this is what you'd like to do as this action can't be undone.`,
      confirm: () => () => {
        deleteItems(selected)
      },
      cancel: () => () => {
        // noop
      },
    })
  }

  const numSelected = Object.values(selected).filter(val => !!val).length

  return (
    <motion.div
      className={classes.root}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
      style={{ overflow: 'hidden' }}
    >
      <AdminHeader>
        <AdminHeader.PageIcon>
          <HeaderIcon color="hsla(194, 47%, 20%, 1)" size={28} />
        </AdminHeader.PageIcon>
        <div className="ml-4">
          <AdminHeader.Title>{t`Templates`}</AdminHeader.Title>
          <AdminHeader.Subtitle>
            {t`Control exactly what users can see and use during the submission process`}
          </AdminHeader.Subtitle>
        </div>
      </AdminHeader>
      <Paper elevation={2} className={classes.paper}>
        {loading ? (
          <div style={{ padding: 24, display: 'flex', width: '100%', justifyContent: 'center' }}>
            <CircularProgress color="primary" />
          </div>
        ) : (
          <div className={classes.table} style={{ height: 'calc(100% - 85px)' }}>
            <EnhancedTableToolbar
              term={searchTerm}
              numSelected={numSelected}
              filterChanged={filterChanged}
              addItem={addItem}
              editItems={editItems}
              deleteItems={deleteItems}
              noAdd={false}
              onReset={onReset}
              blockMultipleEdit={false}
              duplicateItem={duplicateItem}
            >
              <Tooltip title={t`Duplicate`}>
                <Fab
                  style={{
                    color: 'white',
                    marginRight: '10px',
                    boxShadow: 'none',
                  }}
                  color="secondary"
                  size="small"
                  aria-label="Duplicate"
                  className={classes.fab}
                  disabled={numSelected > 1}
                  onClick={duplicateItem}
                >
                  <AddToPhotos />
                </Fab>
              </Tooltip>
              <Tooltip title={t`Edit`}>
                <Fab
                  color="primary"
                  style={{
                    color: 'white',
                    marginRight: '10px',
                    boxShadow: 'none',
                  }}
                  size="small"
                  aria-label="Add"
                  disabled={numSelected > 1}
                  className={classes.fab}
                  onClick={editItems}
                >
                  <Edit />
                </Fab>
              </Tooltip>
              <Tooltip title={t`Delete`}>
                <Fab
                  color="inherit"
                  size="small"
                  aria-label="Add"
                  className={classes.fab}
                  onClick={deleteItemsModal}
                  style={{ boxShadow: 'none', backgroundColor: colors.red.light, color: 'white' }}
                >
                  <Delete />
                </Fab>
              </Tooltip>
            </EnhancedTableToolbar>
            <div style={{ height: 'calc(100% - 64px)' }}>
              <CvsTable
                columns={columns}
                isLoading={isPending}
                rows={displayTemplates}
                orderBy="created_at"
                sortOrder="desc"
                keyField="id"
                noAdd={false}
                addItem={addItem}
                noPagination={true}
                style={{ maxWidth: '100%' }}
                multiSelect={true}
                emptyStateMessage={{
                  icon: (
                    <Fab
                      style={{
                        height: '65px',
                        width: '65px',
                        color: colors.gray.light,
                        cursor: 'auto',
                        boxShadow: 'none',
                      }}
                      disabled
                    >
                      <FileCopyOutlined style={{ fontSize: '34px', color: colors.blue.smokey }} />
                    </Fab>
                  ),
                  title: t`No Templates Found.`,
                  description: t`Try a different search or add a new Template.`,
                }}
                selectAll={val => {
                  setSelected(
                    displayTemplates.reduce(
                      (memo, item) => ({
                        ...memo,
                        [item.id]: val,
                      }),
                      {},
                    ),
                  )
                }}
              >
                {({ row, index }) => (
                  <TableRow
                    hover
                    onClick={() => {
                      navigateToEditTemplate(row.id)
                    }}
                    tabIndex={-1}
                    key={row.id}
                    style={{ cursor: 'pointer', maxWidth: '100%', overflow: 'hidden' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        data-test-page-id={row.name}
                        onClick={e => {
                          e.stopPropagation()
                          setSelected(prev => ({
                            ...prev,
                            [row.id]: !selected[row.id],
                          }))
                        }}
                        checked={selected[row.id]}
                      />
                    </TableCell>
                    <TableCellWithTooltip tooltip={getCellContent('text', row.name)} rowNum={index}>
                      {getCellContent('text', row.name)}
                    </TableCellWithTooltip>
                    <TableCell
                      scope="row"
                      padding="normal"
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {getCellContent('date', row.updated_at)}
                    </TableCell>
                    <TableCell
                      scope="row"
                      padding="normal"
                      style={{
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {getCellContent('date', row.created_at)}
                    </TableCell>
                    <TableCellWithTooltip
                      tooltip={getCellContent('text', row.created_by_user_name)}
                      rowNum={index}
                    >
                      {getCellContent('text', row.created_by_user_name)}
                    </TableCellWithTooltip>
                    <TableCellWithTooltip tooltip={row.patternText || ''} rowNum={index}>
                      {row.patternText}
                    </TableCellWithTooltip>
                    <TableCellWithTooltip tooltip={row.fieldsetText || ''} rowNum={index}>
                      {row.fieldsetText}
                    </TableCellWithTooltip>
                    <TableCellWithTooltip tooltip={row.description || ''} rowNum={index}>
                      {row.description}
                    </TableCellWithTooltip>
                  </TableRow>
                )}
              </CvsTable>
            </div>
          </div>
        )}
      </Paper>
    </motion.div>
  )
}
